import React from 'react';

interface Tool {
    name: string;
    description: string;
    href: string;
    icon: string;
    external?: boolean;
}

const tools: Tool[] = [
    {
        name: 'Service Report',
        description: 'Generate AI-assisted CNC service and installation reports. Manage customer reports, edit drafts, and export to HTML or Markdown.',
        href: '/servicereport',
        icon: '📋',
    },
    {
        name: 'HPM Processor',
        description: 'Convert HypCut technics backups (.hpm/.lcm/.zip) into formatted Excel workbooks with material-specific cutting parameter sheets.',
        href: 'https://hpm-processor.fly.dev',
        icon: '⚙️',
        external: true,
    },
    {
        name: 'HPM Auto Renamer',
        description: 'Merge all cutting conditions into one material category (e.g. ZFactory → ZF, ZSthel → ZS).',
        href: 'https://hpm-processor.fly.dev/renamer',
        icon: '🏷️',
        external: true,
    },
    {
        name: 'Machine Config Translator',
        description: 'Windows app that translates Chinese PLC programs and HMI labels in TubePro / CypCut .cfgpkg machine configs to English. Operator-safe, offline.',
        href: '/Machine_Config_Translator',
        icon: '🌐',
    },
    {
        name: 'XOVR Converter',
        description: 'Collapsible feet/inches ↔ mm widget with built-in AISC channel, I-beam, and H-beam reference charts. Tray-resident, offline Windows app.',
        href: '/XOVR_Converter',
        icon: '📐',
    },
    {
        name: 'TubePro PLC Debugger',
        description: 'Inject debug log steps into every PLC program in a TubePro / CypCut .cfgpkg backup, then load it back to trace exactly which path the PLC is taking. Operator-safe, offline Windows app.',
        href: '/TubePro_PLC_Debugger',
        icon: '🐞',
    },
];

export const LandingPage: React.FC = () => {
    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <header style={styles.header}>
                    <div style={styles.logoRow}>
                        <span style={styles.logoX}>X</span>
                        <span style={styles.logoText}>OVR</span>
                        <span style={styles.logoSlash}>/</span>
                        <span style={styles.logoTools}>tools</span>
                    </div>
                    <p style={styles.tagline}>Internal tools for XOVR CNC application engineering</p>
                </header>

                <div style={styles.grid}>
                    {tools.map((tool) => (
                        <a
                            key={tool.name}
                            href={tool.href}
                            style={styles.card}
                            onMouseEnter={(e) => {
                                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#ff4500';
                                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#2a2a2a';
                                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
                            }}
                        >
                            <div style={styles.cardIcon}>{tool.icon}</div>
                            <div style={styles.cardName}>
                                {tool.name}
                                {tool.external && <span style={styles.externalBadge}>↗</span>}
                            </div>
                            <div style={styles.cardDesc}>{tool.description}</div>
                        </a>
                    ))}
                </div>

                <footer style={styles.footer}>
                    XOVR CNC Machine Tools &amp; Automation
                </footer>
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    page: {
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at top, #1a1a1a 0%, #0a0a0a 100%)',
        color: '#e5e5e5',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '60px 20px',
    },
    container: {
        maxWidth: 920,
        margin: '0 auto',
    },
    header: {
        textAlign: 'center',
        marginBottom: 64,
    },
    logoRow: {
        fontSize: 48,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        display: 'inline-flex',
        alignItems: 'baseline',
    },
    logoX: { color: '#ff4500' },
    logoText: { color: '#ffffff' },
    logoSlash: { color: '#444', margin: '0 4px' },
    logoTools: { color: '#888', fontWeight: 400 },
    tagline: {
        marginTop: 12,
        color: '#888',
        fontSize: 15,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 20,
    },
    card: {
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        padding: '28px 24px',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color 0.15s ease, transform 0.15s ease',
        display: 'block',
    },
    cardIcon: {
        fontSize: 32,
        marginBottom: 16,
    },
    cardName: {
        fontSize: 20,
        fontWeight: 600,
        color: '#ffffff',
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    externalBadge: {
        fontSize: 14,
        color: '#666',
        fontWeight: 400,
    },
    cardDesc: {
        fontSize: 14,
        color: '#999',
        lineHeight: 1.5,
    },
    footer: {
        textAlign: 'center',
        marginTop: 64,
        color: '#555',
        fontSize: 13,
    },
};
